/* eslint-disable */
import React from 'react';
import {DocumentNode} from '@apollo/client';
import SearchBar, {Size} from '../../components/SearchBar/SearchBar';
import {SearchResultItem} from '../../components/SearchBar/SearchBarResults';
import GqlQueryController from 'controllers/GqlQueryController/GqlQueryController';

interface SearchBarControllerProps<T> {
  query: DocumentNode;
  queryVariableName?: string;
  transformResult: (item: T) => SearchResultItem;
  onResultSelect?: (item: T) => void;
  placeholder?: string;
  queryDataPath?: string[];
  searchDelay?: number;
  /** Extra variables merged into every query execution (e.g. countries filter). */
  additionalVariables?: Record<string, unknown>;
  /** Controls the width of the inner SearchBar. Defaults to 'small'. */
  size?: Size;
}

interface SearchBarInnerProps {
  setQueryVars: Function;
  searchValue: string;
  queryVariableName: string;
  placeholder?: string;
  onSearch: (value: string) => void;
  results: SearchResultItem[];
  onResultSelect: (item: SearchResultItem) => void;
  searchDelay?: number;
  additionalVariables?: Record<string, unknown>;
  size?: Size;
}

const SearchBarInner: React.FC<SearchBarInnerProps> = ({
  setQueryVars,
  searchValue,
  queryVariableName,
  placeholder,
  onSearch,
  results,
  onResultSelect,
  searchDelay,
  additionalVariables,
  size,
}) => {
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setQueryVars({[queryVariableName]: searchValue, ...additionalVariables});
    }, 300);
    return () => clearTimeout(timer);
  }, [searchValue, setQueryVars, queryVariableName, additionalVariables]);

  return (
    <SearchBar
      placeholder={placeholder || 'Search...'}
      onSearch={onSearch}
      results={results}
      onResultSelect={onResultSelect}
      searchOnKeyUp={true}
      size={size ?? 'small'}
      searchDelay={searchDelay}
    />
  );
};

const SearchBarController = <T extends {id: string | number}>({
  query,
  queryVariableName = 'data',
  transformResult,
  onResultSelect,
  placeholder,
  searchDelay,
  additionalVariables,
  size,
}: SearchBarControllerProps<T>) => {
  const [results, setResults] = React.useState<T[]>([]);
  const [searchValue, setSearchValue] = React.useState('');

  const paramValidator = React.useCallback(
    (old: any, current: any) => {
      if (current[queryVariableName].trim() === '') {
        return false;
      } else if (current[queryVariableName].length < 3) {
        setResults([]);
        return false;
      }
      return old[queryVariableName] !== current[queryVariableName];
    },
    [queryVariableName]
  );

  const transformResults = React.useCallback(
    (data: T[]): SearchResultItem[] => data.map(transformResult),
    [transformResult]
  );

  const handleResultSelect = React.useCallback(
    (item: SearchResultItem) => {
      const result = results.find(i => i.id === item.id);
      if (result && onResultSelect) {
        onResultSelect(result);
      }
    },
    [results, onResultSelect]
  );

  const handleSearch = React.useCallback((data: string) => {
    setSearchValue(data.trim());
  }, []);

  const initialVars = React.useMemo(
    () => ({[queryVariableName]: '', ...additionalVariables}),
    [queryVariableName, additionalVariables]
  );

  return (
    <GqlQueryController
      QUERY={query}
      variables={initialVars}
      rootField="filterUsers"
      initialState={results}
      paramValidator={paramValidator}
      onSuccess={(data: T[]) => {
        setResults(data);
      }}
    >
      {({setQueryVars}) => (
        <SearchBarInner
          setQueryVars={setQueryVars}
          searchValue={searchValue}
          queryVariableName={queryVariableName}
          placeholder={placeholder}
          onSearch={handleSearch}
          results={transformResults(results)}
          onResultSelect={handleResultSelect}
          searchDelay={searchDelay}
          additionalVariables={additionalVariables}
          size={size}
        />
      )}
    </GqlQueryController>
  );
};

export default SearchBarController as <T extends {id: string | number}>(
  props: SearchBarControllerProps<T>
) => React.ReactElement;
